/**
 * What every kind of thing that is shot at but does not fly *is*: a gasbag in
 * the air, or a structure on the ground.
 *
 * Split out of `targets.ts` when the ground kinds arrived, so that file stays
 * the entity mechanics (create, drift, take damage, burn) and this one is the
 * catalog. `targets.ts` re-exports everything here, so no consumer had to move.
 *
 * ## The two classes
 *
 * **Airborne** — the observation balloon and the Zeppelin. Deliberately not
 * modelled as aircraft: `SimState` is an aeroplane, and the game ships 33
 * `<PLANE>.DAT` files and no `BALLOON.DAT`, so forcing a gasbag through
 * `AIRCRAFT_ROWS` would mean inventing fields for a file that does not exist.
 *
 * **Ground structures** — what a bombing raid is flown against, and a declared
 * port extension. The manual names the raid's own three verbatim — *"Your
 * bombers are about to penetrate deep into enemy territory to bomb strategic
 * targets like RR yards, factories, and supply dumps"*
 * (docs/game/manual/04-gameplay.md), and `PS.EXE`'s briefing-substitution pool
 * carries the same three — so those three are the ones this port places.
 *
 * **Ground, moving** — the `train`, and it is *not* an extension: it is the
 * original's object **class 5**, recovered in full. `RB.EXE`'s class-name
 * function returns `"TRAIN"` for class 5, `pyro.c`'s kill-noun table has
 * `train` at index 5, scenario opcode 5 spawns one from a 25-byte
 * start/end route record, `intel/move.c` walks it along that route every tick,
 * and the shipped `SCRIPT.DAT` contains **two of them**. See `ground-route.ts`
 * for the mechanics and docs/game/ground-traffic.md for the evidence with
 * offsets.
 *
 * **`SUPPLY TRUCK` and `TANK` are deliberately absent.** They are a real class
 * — class 3, with a size, a name pair and the kill noun `truck` — but their
 * scenario constructor slot is a **NULL pointer**, so the shipped original
 * cannot create one either. There is also no vehicle model anywhere in the
 * 804-object corpus. Building them would be inventing content rather than
 * porting it, so they are named here rather than half-built.
 *
 * ## Geometry provenance, stated
 *
 * The buildings are real: `TERR1.TBL` is an object library of 37 models and its
 * visual census (docs/formats/models-tbl.md §, from a render sheet) reads *"a
 * factory with a chimney, hangars, a church with a cross on the spire, airfields
 * with marked runways… a bridge over a river"*. **Which slot is which is not
 * decoded.** The census is a left-to-right reading of a sheet with no index
 * mapping, and docs/formats/world-terrain.md §2.4 is explicitly open on the same
 * question ("real airfields are very likely built from several distinct objects
 * … not one reused 'airfield' model"). So the model each ground kind draws with
 * is chosen **by measured size** off the baked corpus, and that is a port-side
 * choice, not a claim that `TERR1#52` is the factory:
 *
 * | kind | model | measured extent (m) | why this one |
 * | --- | --- | --- | --- |
 * | `factory` | `models/TERR1-52` | 41.8 × 36.9 × **30.8** | the corpus's tallest structure, and 30 m is `.GI`'s own verified tallest building (gi-format.md §7.4) |
 * | `railYard` | `models/TERR1-68` | **61.3** × 12.3 × 3.4 | the longest low structure — a shed row along a track |
 * | `supplyDump` | `models/TERR1-33` | 41.0 × 16.4 × 4.6 | a smaller low block of the same family |
 *
 * `centreHeightM` is half of that measured height, because the renderer
 * recentres a baked model on its own bounding box: a structure placed at
 * `y = centreHeightM` stands on the ground instead of being buried to the eaves.
 */

import type { Vec3 } from "../flight/math3d.ts";
import type {
  AirborneTargetKind,
  AmmunitionType,
  GroundTargetKind,
  MobileGroundTargetKind,
  TargetKind,
  TargetProfile,
} from "./target-kinds.ts";

// The taxonomy travels with the catalog, so a consumer that wants "what a
// target is" still has one import — the split is a size ceiling, not an API.
export {
  AIRBORNE_TARGET_KINDS,
  AMMUNITION_TYPES,
  DEFENCE_TARGET_KINDS,
  GROUND_TARGET_KINDS,
  MOBILE_GROUND_TARGET_KINDS,
  TARGET_KINDS,
} from "./target-kinds.ts";
export type {
  AirborneTargetKind,
  AmmunitionType,
  DefenceTargetKind,
  GroundTargetKind,
  MobileGroundTargetKind,
  TargetClass,
  TargetKillCategory,
  TargetKind,
  TargetProfile,
} from "./target-kinds.ts";

interface AirborneSeed {
  readonly kind: AirborneTargetKind;
  readonly displayName: string;
  readonly bakedModelId: string;
  readonly targetSizeRaw: number;
  readonly driftSpeedMps: number;
  readonly regular: number;
  readonly incendiary: number;
  readonly burnSeconds: number;
  readonly burnDescentMps: number;
  readonly killCategory: "balloons" | "zeppelins";
}

function airborne(seed: AirborneSeed): TargetProfile {
  return {
    kind: seed.kind,
    targetClass: "airborne",
    displayName: seed.displayName,
    bakedModelId: seed.bakedModelId,
    targetSizeRaw: seed.targetSizeRaw,
    driftSpeedMps: seed.driftSpeedMps,
    damagePerHit: { regular: seed.regular, incendiary: seed.incendiary },
    bombVulnerability: 0,
    footprintRadiusM: 0,
    centreHeightM: 0,
    burnSeconds: seed.burnSeconds,
    burnDescentMps: seed.burnDescentMps,
    killCategory: seed.killCategory,
    followsRoute: false,
    emitsSmoke: false,
    strategic: true,
  };
}

interface GroundSeed {
  readonly kind: GroundTargetKind | MobileGroundTargetKind;
  readonly displayName: string;
  readonly bakedModelId: string;
  readonly footprintRadiusM: number;
  readonly heightM: number;
  /** Bombs of the reference load needed to level it, at the centre of the blast. */
  readonly bombsToLevel: number;
  readonly burnSeconds: number;
  /** Route speed, m/s. Non-zero only for the train. */
  readonly speedMps?: number;
  readonly followsRoute?: boolean;
  readonly emitsSmoke?: boolean;
  /** Overrides the structure figures — a train is far softer than a building. */
  readonly damagePerHit?: Readonly<Record<AmmunitionType, number>>;
}

/**
 * A structure is *tough against rifle-calibre fire and soft against bombs*,
 * which is the whole reason a bombing raid is flown rather than a strafing run.
 * The machine-gun figures are deliberately tiny rather than zero — Bishop's VC
 * was won shooting up an aerodrome, so a determined pilot with a full belt
 * should be able to set a supply dump alight and find it takes most of the
 * sortie. CALIBRATION-PENDING, like every other damage number in this port.
 */
const MG_DAMAGE_PER_HIT: Readonly<Record<AmmunitionType, number>> = {
  regular: 0.0009,
  incendiary: 0.0016,
};

function ground(seed: GroundSeed): TargetProfile {
  return {
    kind: seed.kind,
    targetClass: "ground",
    displayName: seed.displayName,
    bakedModelId: seed.bakedModelId,
    // A structure is not shot at with a lead solution, but gunnery still needs a
    // size for it; its footprint in the same raw units the fleet table uses
    // (0.035 m per unit) keeps one convention rather than two.
    targetSizeRaw: Math.round(seed.footprintRadiusM / 0.035),
    driftSpeedMps: seed.speedMps ?? 0,
    damagePerHit: seed.damagePerHit ?? MG_DAMAGE_PER_HIT,
    bombVulnerability: 1 / Math.max(seed.bombsToLevel, 1),
    footprintRadiusM: seed.footprintRadiusM,
    centreHeightM: seed.heightM / 2,
    burnSeconds: seed.burnSeconds,
    burnDescentMps: 0,
    killCategory: null,
    followsRoute: seed.followsRoute ?? false,
    emitsSmoke: seed.emitsSmoke ?? false,
    strategic: true,
  };
}

/**
 * The one defence fitting: a 90 cm carbon-arc projector on its tripod, the
 * hardware AD-017 §10 modelled and §11 made shootable.
 *
 * Every number is the period machine rather than a `.DAT` field, because there
 * is no `.DAT` field — the original has no such object. A 90 cm light with its
 * yoke and foot is a little over two metres across and about three tall, and it
 * stands beside a generator lorry and a crew; `footprintRadiusM` is that
 * emplacement rather than the drum alone, which is what makes it a *hard but
 * possible* strafing target: 100 raw units against a scout's ~230.
 *
 * It is soft in a way no structure is. A searchlight is a parabolic mirror, a
 * glass door and a thin-plate drum around an open arc — a handful of rifle-
 * calibre rounds through it and there is nothing left to point. Eleven hits at
 * `regular`, seven at `incendiary`: about one determined pass.
 * CALIBRATION-PENDING, like every other damage number in this file.
 *
 * `burnSeconds` is deliberately short. The arc house flares and goes out; it
 * does not sit burning like a supply dump, and the half-second is there only so
 * the beam's extinction and the wreck's appearance are two frames apart rather
 * than one.
 */
function searchlight(): TargetProfile {
  return {
    kind: "searchlight",
    targetClass: "ground",
    displayName: "Searchlight",
    bakedModelId: null,
    targetSizeRaw: Math.round(3.5 / 0.035),
    driftSpeedMps: 0,
    damagePerHit: { regular: 0.09, incendiary: 0.14 },
    // One bomb anywhere near a tripod and a mirror is the end of it.
    bombVulnerability: 1,
    footprintRadiusM: 3.5,
    // Half the projector's own height: `searchlight-mount.ts` recentres nothing,
    // but the collision sphere should sit at the drum rather than at the feet.
    centreHeightM: 1.5,
    burnSeconds: 0.5,
    burnDescentMps: 0,
    killCategory: null,
    followsRoute: false,
    emitsSmoke: false,
    strategic: false,
  };
}

export const TARGET_PROFILES: Readonly<Record<TargetKind, TargetProfile>> = {
  balloon: airborne({
    kind: "balloon",
    displayName: "Observation balloon",
    // BALLOON.TBL became multi-object once the directory walk ran to the null
    // dword (models-tbl.md §"directory"), so the baked id carries the -00 suffix.
    bakedModelId: "models/BALLOON-00",
    targetSizeRaw: 380,
    driftSpeedMps: 0,
    regular: 0.004,
    incendiary: 0.05,
    burnSeconds: 6,
    burnDescentMps: 9,
    killCategory: "balloons",
  }),
  zeppelin: airborne({
    kind: "zeppelin",
    displayName: "Zeppelin",
    bakedModelId: "models/ZEPPELIN",
    targetSizeRaw: 1600,
    driftSpeedMps: 12,
    regular: 0.0018,
    incendiary: 0.02,
    burnSeconds: 14,
    burnDescentMps: 6,
    killCategory: "zeppelins",
  }),
  supplyDump: ground({
    kind: "supplyDump",
    displayName: "Supply dump",
    bakedModelId: "models/TERR1-33",
    footprintRadiusM: 22,
    heightM: 4.6,
    bombsToLevel: 2,
    burnSeconds: 8,
  }),
  factory: ground({
    kind: "factory",
    displayName: "Factory",
    bakedModelId: "models/TERR1-52",
    footprintRadiusM: 28,
    heightM: 30.8,
    bombsToLevel: 4,
    burnSeconds: 12,
  }),
  railYard: ground({
    kind: "railYard",
    displayName: "Railway yard",
    bakedModelId: "models/TERR1-68",
    footprintRadiusM: 31,
    heightM: 3.4,
    bombsToLevel: 2,
    burnSeconds: 10,
  }),
  train: ground({
    kind: "train",
    displayName: "Train",
    // Chosen by measured size off the baked corpus, exactly as the three
    // structures above were, and with the same disclaimer: the original draws
    // its train from a scenery library too (`modelTable[1] + 0x11`) but which
    // slot is not decoded. `TERR1-20` is the corpus's longest narrow solid —
    // 92.6 x 7.9 x 6.2 m, an 11.7:1 body — which is a locomotive and its
    // carriages at this engine's fidelity. It is *not* a claim that
    // `TERR1#20` is the train.
    bakedModelId: "models/TERR1-20",
    // The body is long and thin; a blast radius of half its length would swallow
    // anything parked beside the line, so this takes the narrower dimension's
    // scale and lets `bombsToLevel: 1` do the killing.
    footprintRadiusM: 30,
    heightM: 6.2,
    // One bomb finishes a train. That is the whole point of bombing one, and it
    // is what the original's blast handler does: a class-5 object inside the
    // radius is flagged dead and retired on the spot (file `0x1E7F4`).
    bombsToLevel: 1,
    burnSeconds: 10,
    // ONE global speed for every train (`ds:0x4d42`, read fresh each tick in
    // `move.c`), so this is a constant rather than a per-train field. 11 m/s is
    // ~40 km/h, a loaded WWI supply train. CALIBRATION-PENDING like every other
    // number in this file.
    speedMps: 11,
    followsRoute: true,
    emitsSmoke: true,
    // Far softer than a structure: strafing trains is the thing the history
    // chapter keeps describing, so a determined pass with a full belt should
    // actually finish one rather than take the whole sortie.
    // CALIBRATION-PENDING.
    damagePerHit: { regular: 0.005, incendiary: 0.009 },
  }),
  searchlight: searchlight(),
};

export function targetProfileOf(kind: TargetKind): TargetProfile {
  return TARGET_PROFILES[kind];
}

export function isGroundTargetKind(kind: TargetKind): boolean {
  return TARGET_PROFILES[kind].targetClass === "ground";
}

/** Whether the fight's outcome, the raid tally and the briefing map care about this kind — see `TargetProfile.strategic`. */
export function isStrategicTargetKind(kind: TargetKind): boolean {
  return TARGET_PROFILES[kind].strategic;
}

/** Where a structure of this kind stands when its footprint is on the ground at `groundY`. */
export function groundTargetPosition(kind: TargetKind, x: number, z: number, groundY = 0): Vec3 {
  return { x, y: groundY + TARGET_PROFILES[kind].centreHeightM, z };
}
